import {
  MutationFunction,
  QueryKey,
  useMutation as reactQueryMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { extractReactQueryKey } from 'utils';

type Item = {
  id?: number;
};

export const useOptimisticUpdate = <
  TData extends Item,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  key: QueryKey,
  query: MutationFunction<TData, TVariables>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>,
) => {
  const queryClient = useQueryClient();

  return reactQueryMutation({
    mutationFn: query,
    ...options,
    onSuccess: (newItem, b, c, d) => {
      queryClient.invalidateQueries({ queryKey: extractReactQueryKey(key) });

      // optimistic update of all items
      queryClient.setQueryData<TData[] | TData>(extractReactQueryKey(key), (old) => {
        const oldItems = old ?? [];
        if (!newItem) return oldItems;

        if (!Array.isArray(oldItems)) return { ...oldItems, ...newItem };

        let itemExists = false;
        // if item already downloaded, replace it
        const output = oldItems.map((x) => {
          if (x.id === newItem.id) {
            itemExists = true;
            return { ...x, ...newItem };
          }
          return x;
        });

        if (itemExists) return output;

        // if new item, add it to store
        return [...oldItems, newItem];
      });

      // optimistic update of item
      queryClient.setQueryData<TData>(extractReactQueryKey([key, newItem?.id]), (old) => {
        if (!newItem) return old;

        return newItem;
      });

      options?.onSuccess?.(newItem, b, c, d);
    },
  });
};
